import React, {useEffect, useState} from 'react';
import Datepicker from '../Datepicker';
import moment from 'moment';
import Input from '../Input';
import {
  useCreateHoseEquipmentMutation,
  useDeleteHoseEquipmentMutation,
  useUpdateHoseEquipmentMutation,
} from '../../store/builder/inspections.api';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {toast} from 'react-toastify';
import {setLoader} from '../../store/builder/reducer';
import {useDispatch} from 'react-redux';
import {useParams} from 'react-router-dom';
import useLoader from '../../global/UseLoadingHandler';
import {checkFileExtension} from '../../global/utils';
import {useTranslation} from '../../hooks/useTranslation';

const HoseElement = ({hose, setHoseFields, index, getEquipment, readOnly = false}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const [date, setDate] = useState();
  const [name, setName] = useState('');
  const [actionStatus, setActionStatus] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState(false);
  const [selectedImage, setSelectedImage] = useState({
    show_img: null,
    img_file: null,
  });

  const [
    updateHoseEquipment,
    {
      isLoading: updateHoseEquipmentIsLoading,
      error: updateHoseEquipmentError,
      isError: updateHoseEquipmentIsError,
      isSuccess: updateHoseEquipmentIsSuccess,
    },
  ] = useUpdateHoseEquipmentMutation();

  const [
    deleteHoseEquipment,
    {
      isLoading: deleteHoseEquipmentIsLoading,
      error: deleteHoseEquipmentError,
      isError: deleteHoseEquipmentIsError,
      isSuccess: deleteHoseEquipmentIsSuccess,
    },
  ] = useDeleteHoseEquipmentMutation();

  const [
    createHoseEquipment,
    {
      isLoading: createHoseEquipmentIsLoading,
      error: createHoseEquipmentError,
      isError: createHoseEquipmentIsError,
    },
  ] = useCreateHoseEquipmentMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    updateHoseEquipmentError,
    deleteHoseEquipmentError,
    createHoseEquipmentError,
  ]);

  useLoader([deleteHoseEquipmentIsLoading, createHoseEquipmentIsLoading]);

  useEffect(() => {
    dispatch(setLoader(updateHoseEquipmentIsLoading));
    if (updateHoseEquipmentIsSuccess) {
      dispatch(setLoader(false));
      getEquipment();
      toast.success('Hose updated successfully!');
      setHoseFields(old =>
        old.map((el, idx) => {
          if (el.id === hose.id) {
            return {...el, edit_status: 'no-edit'};
          }
          return el;
        }),
      );
    }
  }, [updateHoseEquipmentIsLoading, updateHoseEquipmentIsSuccess]);

  useEffect(() => {
    setSelectedImage(old => ({...old, show_img: hose?.cert_image}));
    setName(hose.name);
    setDate(hose?.manufacture_date);
  }, []);

  useEffect(() => {
    setHoseFields(old =>
      old.map((el, idx) => {
        if (el.id === hose.id) {
          return {
            ...el,
            manufacture_date: moment(date).format('YYYY-MM-DD'),
            name: name,
            cert_image: selectedImage.img_file,
          };
        }
        return el;
      }),
    );
  }, [date, name, selectedImage.img_file]);

  const handleName = e => {
    setName(e.target.value);
  };

  const handleImageChange = event => {
    const file = event.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      if (checkFileExtension(file?.name) === 'pdf') {
        setSelectedImage(old => ({
          ...old,
          show_img: file?.name,
          img_file: file,
        }));
      } else {
        setSelectedImage(old => ({
          ...old,
          show_img: imageUrl,
          img_file: file,
        }));
      }
    }
  };

  const handleHoseSubmit = () => {
    setActionStatus(false);
    const formData = new FormData();
    if (selectedImage.img_file) {
      formData.append(
        'cert_image',
        selectedImage.img_file
          ? selectedImage.img_file
          : selectedImage?.show_img,
      );
    }
    formData.append('equipment_id', params?.id);
    formData.append('name', name ? name : '');
    formData.append(
      'manufacture_date',
      date ? moment(date).format('YYYY-MM-DD') : '',
    );
    updateHoseEquipment({id: hose?.id, body: formData});
  };

  const renderDeleteHose = () => {
    return (
      <div className='modal'>
        <div className='modal__content'>
          <div className='modal__content-head'>
            <p>{t('hoseElement.modals.deleteTitle')}</p>
            <img
              className='modal__content-exit'
              src='/assets/icons/close.svg'
              alt='close'
              onClick={() => setDeleteStatus(false)}
            />
          </div>
          <div
            className='modal__content-body'
            style={{display: 'flex', justifyContent: 'center'}}
          >
            {t('hoseElement.modals.deleteBody')}
          </div>
          <div className='modal__content-bottom'>
            <button className='close' onClick={() => setDeleteStatus(false)}>
              {t('hoseElement.actions.cancel')}
            </button>
            <button className='save danger' onClick={handleHoseDelete}>
              {t('hoseElement.actions.delete')}
            </button>
          </div>
        </div>
        <div className='modal__bg'></div>
      </div>
    );
  };

  const handleHoseDelete = () => {
    deleteHoseEquipment({id: hose?.id}).then(res => {
      dispatch(setLoader(false));
      toast.success(res.data.message);
      setDeleteStatus(false);
      getEquipment(params.id);
    });
  };

  const handleHoseCreate = () => {
    if (!name) {
      toast.error(t('hoseElement.messages.idRequired'));
      return;
    }
    const formData = new FormData();
    formData.append(`items[${0}][name]`, name ? name : '');
    formData.append(
      `items[${0}][manufacture_date]`,
      date && date !== 'Invalid date' ? moment(date).format('YYYY-MM-DD') : '',
    );
    formData.append(`items[${0}][equipment_id]`, params?.id);
    if (selectedImage.img_file) {
      formData.append(`items[${0}][cert_image]`, selectedImage.img_file);
    }

    createHoseEquipment(formData).then(res => {
      dispatch(setLoader(false));
      toast.success(res.data.message);
      getEquipment(params.id);
      setHoseFields([]);
    });
  };

  return (
    <div className='equipmentHose__element' key={hose?.id}>
      {deleteStatus && renderDeleteHose()}
      {actionStatus && (
        <div className='equipmentHose__element-dropdown'>
          <div
            className='equipmentHose__element-dropdown-item'
            onClick={() => {
              if (hose?.edit_status === 'no-edit') {
                setActionStatus(false);
                setHoseFields(old =>
                  old.map((el, idx) => {
                    if (el.id === hose.id) {
                      return {...el, edit_status: 'edit'};
                    }
                    return el;
                  }),
                );
              }
            }}
          >
            <img src='/assets/icons/edit.svg' alt='icon' />
            <span>{t('hoseElement.actions.edit')}</span>
          </div>
          <div
            className='equipmentHose__element-dropdown-item'
            onClick={() => setDeleteStatus(true)}
          >
            <img src='/assets/icons/trash-hose.svg' alt='icon' />
            <span>{t('hoseElement.actions.delete')}</span>
          </div>
        </div>
      )}
      <div className='equipmentHose__element-top'>
        <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
          <p className='equipmentHose__element-title'>{t('hoseElement.labels.hoseId')}:</p>
          <Input
            readOnly={readOnly || hose?.edit_status === 'no-edit'}
            placeholder={t('hoseElement.placeholders.hoseId')}
            id='name'
            name='name'
            type='text'
            onChange={handleName}
            defaultValue={hose?.name}
          />
        </div>
        {!readOnly && hose?.edit_status !== 'new' && (
          <span
            className='equipmentHose__element-actions'
            onClick={() => setActionStatus(!actionStatus)}
          >
            {/*{hose?.edit_status === 'no-edit' ? 'Edit' : hose?.edit_status === 'edit' ? 'Save' : '' }*/}
            <img src='/assets/icons/dots.svg' alt='dots' />
          </span>
        )}
      </div>
      <label
        htmlFor={hose?.edit_status !== 'no-edit' ? 'hose_file_' + hose?.id : ''}
        className='equipmentHose__element-file'
      >
        {!selectedImage?.show_img && !selectedImage?.img_file && !readOnly && hose?.edit_status !== 'no-edit' && (
          <span>{t('hoseElement.upload.uploadFile')}</span>
        )}
        {!selectedImage?.show_img && !selectedImage?.img_file && hose?.edit_status === 'no-edit' && (
          <div className='equipmentHose__element-img-placeholder'>
            <img src='/assets/icons/image.svg' alt='no image' />
            <span>{t('hoseElement.upload.noImage')}</span>
          </div>
        )}
        {selectedImage?.show_img || selectedImage?.img_file ? (
          checkFileExtension(selectedImage?.show_img) === 'pdf' ? (
            <a
              className='equipmentHose__element-img'
              href={selectedImage?.show_img}
              target='_blank' rel="noreferrer"
            >
              <img
                className='equipmentHose__element-img pdf'
                src={'/assets/icons/pdf-icon.svg'}
                alt='selectedImage'
              />
            </a>
          ) : (
            <img
              className='equipmentHose__element-img'
              src={selectedImage?.show_img}
              alt='selectedImage'
            />
          )
        ) : null}
        <input
          readOnly={readOnly || hose?.edit_status === 'no-edit'}
          type='file'
          id={'hose_file_' + hose?.id}
          onChange={handleImageChange}
          disabled={readOnly}
        />
        {hose?.edit_status !== 'no-edit' && (
          <div>
            <p>{t('hoseElement.upload.chooseFile')}</p>
          </div>
        )}
      </label>
      <div className='equipmentHose__element-date'>
        <span>{t('hoseElement.labels.dateOfManufacture')}</span>
        <Datepicker
          readOnly={readOnly || hose?.edit_status === 'no-edit'}
          width={'100%'}
          setValue={setDate}
          value={date}
        />
      </div>
      {!readOnly && (hose?.edit_status === 'edit' || hose?.edit_status === 'new') && (
        <div className='equipmentHose__element-btns'>
          <div
            className='equipmentHose__element-btn cancel'
            onClick={() => {
              setHoseFields(old =>
                old.map((el, idx) => {
                  if (el.id === hose.id) {
                    return {...el, edit_status: 'no-edit'};
                  }
                  return el;
                }),
              );
            }}
          >
            {t('hoseElement.actions.cancel')}
          </div>
          <div
            className='equipmentHose__element-btn submit'
            onClick={() => {
              if (hose.edit_status === 'new') {
                handleHoseCreate();
              } else {
                handleHoseSubmit();
              }
            }}
          >
            {hose?.edit_status === 'new' ? t('hoseElement.actions.create') : t('hoseElement.actions.save')}
          </div>
        </div>
      )}
    </div>
  );
};

export default HoseElement;
